import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import accounting from 'accounting';
import ExternalLinkIcon from 'components/v2/icons/ExternalLinkIcon';
import Pagination from 'components/v2/Pagination';
import formatMoney from 'utils/formatters/Money';
import EditButton from '../../../v2/buttons/EditButton';
import ExclamationIcon from '../../../v2/icons/ExclamationIcon';
import Tooltip from '../../../v2/Tooltip';
import SessionStore from '../../../../stores/SessionStore';
import { LocaleContext } from 'providers/locale-settings-context';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
const MARGIN_THRESHOLD = 15;
function renderExclamationIcon() {
    return React.createElement(ExclamationIcon, { className: "w-5 h-5 text-red-700" });
}
function Link({ children, url }) {
    return (React.createElement("a", { "data-testid": "a-217aef46", href: url, target: "_blank", rel: "noopener noreferrer" },
        React.createElement("div", { "data-testid": "div-ed4eed3c", className: "flex items-center space-x-1" },
            React.createElement("span", { "data-testid": "span-376212ac", className: "flex-1" }, children),
            React.createElement(ExternalLinkIcon, { className: "w-4 h-4 flex-shrink-0" }))));
}
function renderEditButton(item, i, handleEditButtonClick) {
    return function () {
        return (React.createElement(EditButton, { dataTestId: "EditButton-3533181c", disabled: !SessionStore.isAbleToAdjustCarrierBulkCost(), onClick: handleEditButtonClick(item, i) }));
    };
}
export default function List({ items, perPage, page, pages, total, onPageClick, onEdit, onViewShipments, }) {
    const { t } = useTranslation();
    const { formatDate } = useContext(LocaleContext);
    function handlePageClick(selectedPage) {
        onPageClick(selectedPage);
    }
    function handleEditButtonClick(item, index) {
        return function () {
            onEdit(item, index);
        };
    }
    function handleViewShipments(item, index) {
        return function (e) {
            e.preventDefault();
            onViewShipments(item, index);
        };
    }
    function renderMargin(item) {
        const tooltipId = `margin-tooltip-${item.id}`;
        if (typeof item.margin === 'undefined' || item.margin === null) {
            return (React.createElement(Tooltip, { dataTestId: "Tooltip-5a1a6947", id: tooltipId, body: renderExclamationIcon }, t('BELOW_MARGIN_THRESHOLD', {
                threshold: MARGIN_THRESHOLD,
            })));
        }
        const belowThreshold = item.margin < MARGIN_THRESHOLD;
        return (React.createElement("div", { "data-testid": "div-8d8c1e6f", className: classnames('flex items-center space-x-1', {
                'text-red-800': belowThreshold,
            }) },
            belowThreshold && (React.createElement(Tooltip, { dataTestId: "Tooltip-9587dcfb", id: tooltipId, body: renderExclamationIcon }, t('BELOW_MARGIN_THRESHOLD', {
                threshold: MARGIN_THRESHOLD,
            }))),
            React.createElement("span", { "data-testid": "span-e6d83a8b" },
                accounting.formatNumber(item.margin),
                "%")));
    }
    const ccbatEnabled = featureEnabled(CONFIGURED_FEATURES.CCBAT);
    return (React.createElement("div", { "data-testid": "div-4ebab118", className: "overflow-x-auto" },
        React.createElement("table", { "data-testid": "table-fd070433", className: "default-table" },
            React.createElement("thead", { "data-testid": "thead-77555c5a" },
                React.createElement("tr", { "data-testid": "tr-1cd869de" },
                    React.createElement("th", { "data-testid": "th-ab8b3d9c" }, t('SHIPPER')),
                    React.createElement("th", { "data-testid": "th-e0d80364" }, t('ORIGIN')),
                    React.createElement("th", { "data-testid": "th-2fde5d1a" }, t('DESTINATION')),
                    React.createElement("th", { "data-testid": "th-7bc8e8c6" }, t('CARRIER')),
                    React.createElement("th", { "data-testid": "th-321f5376" }, t('SHIPMENT_COUNT')),
                    React.createElement("th", { "data-testid": "th-91f74cff" }, t('SHIPPER_RATE')),
                    React.createElement("th", { "data-testid": "th-9f67b1ab" }, t('CARRIER_RATE')),
                    ccbatEnabled && (React.createElement(React.Fragment, null,
                        React.createElement("th", { "data-testid": "th-fc86c001" }, t('CARRIER_FSC')),
                        React.createElement("th", { "data-testid": "th-1ab49a0c" }, t('TOTAL')),
                        React.createElement("th", { "data-testid": "th-eefd3072" }, t('APPROVAL_DUE_DATE')))),
                    React.createElement("th", { "data-testid": "th-05e34584" }, t('MARGIN')),
                    React.createElement("th", { "data-testid": "th-ee3d40b7" }, t('ACTIONS')))),
            React.createElement("tbody", { "data-testid": "tbody-7aa6d674" }, items.map((item, i) => {
                var _a, _b, _c, _d;
                return (React.createElement("tr", { "data-testid": `tr-445a7749-${i}`, key: item.id },
                    React.createElement("td", { "data-testid": `td-c829514b-${i}` },
                        React.createElement(Link, { url: `/users/${(_a = item.shipper) === null || _a === void 0 ? void 0 : _a._id}` }, (_b = item.shipper) === null || _b === void 0 ? void 0 : _b.shipper_name)),
                    React.createElement("td", { "data-testid": `td-a3e71a61-${i}` }, item.originAddress),
                    React.createElement("td", { "data-testid": `td-838d8711-${i}` }, item.destinationAddress),
                    React.createElement("td", { "data-testid": `td-475c9720-${i}` },
                        React.createElement(Link, { url: `/users/${(_c = item.carrier) === null || _c === void 0 ? void 0 : _c._id}` }, (_d = item.carrier) === null || _d === void 0 ? void 0 : _d.carrier_name)),
                    React.createElement("td", { "data-testid": `td-336fe74b-${i}` },
                        React.createElement("a", { "data-testid": `a-9fe5e00c-${i}`, href: "#", onClick: handleViewShipments(item, i) }, item.count)),
                    React.createElement("td", { "data-testid": `td-df96f793-${i}` }, formatMoney(item.shipper_base_cost, item === null || item === void 0 ? void 0 : item.currency)),
                    React.createElement("td", { "data-testid": `td-7ff54a96-${i}` }, formatMoney(item.carrier_base_cost, item === null || item === void 0 ? void 0 : item.currency)),
                    ccbatEnabled && (React.createElement(React.Fragment, null,
                        React.createElement("td", { "data-testid": `td-8cdfe81e-${i}`, className: "text-center" }, (item === null || item === void 0 ? void 0 : item.carrierFscTotal)
                            ? formatMoney(item === null || item === void 0 ? void 0 : item.carrierFscTotal, item === null || item === void 0 ? void 0 : item.currency)
                            : '-'),
                        React.createElement("td", { "data-testid": `td-d7be4174-${i}`, className: "text-center" }, (item === null || item === void 0 ? void 0 : item.carrierFscTotal)
                            ? formatMoney(item.carrierFscTotal + item.carrier_base_cost, item === null || item === void 0 ? void 0 : item.currency)
                            : formatMoney(item.carrier_base_cost, item === null || item === void 0 ? void 0 : item.currency)),
                        React.createElement("td", { "data-testid": `td-dd94fd1c-${i}`, className: "text-center" }, (item === null || item === void 0 ? void 0 : item.approvalDueDate)
                            ? formatDate(item === null || item === void 0 ? void 0 : item.approvalDueDate)
                            : '-'))),
                    React.createElement("td", { "data-testid": `td-43a8a4f4-${i}` }, renderMargin(item)),
                    React.createElement("td", { "data-testid": `td-0a82f048-${i}` },
                        React.createElement("div", { "data-testid": `div-8f778537-${i}`, className: "flex space-x-2 items-center" },
                            React.createElement(Tooltip, { dataTestId: `Tooltip-034c5b38-${i}`, id: `edit-tooltip-${item.id}`, position: "top", body: renderEditButton(item, i, handleEditButtonClick) }, t('EDIT_RATE'))))));
            }))),
        total > 0 && (React.createElement(Pagination, { dataTestId: "Pagination-ff8a63ff", className: "", perPage: perPage, page: page, pages: pages, total: total, onPageClick: handlePageClick }))));
}
